import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import { useHasPermissionsCheck } from "@ryde/shared";

import { FilterSearch } from "../input-fields";
import { BulkActionsPanelStyled } from "./bulk-actions-panel.styled";
import { BulkActionsRow } from "./bulk-actions-row";
import { BULK_ACTIONS_PANEL_CONFIG } from "./bulk-actions.constants";
import { IBulkActionItem } from "./bulk-actions.models";
import { filterBulkActions } from "./bulk-actions.utils";

interface IBulkActionsPanelProps {
  actions: IBulkActionItem[];
}

export const BulkActionsPanel: FC<IBulkActionsPanelProps> = ({ actions }) => {
  const { t } = useTranslation();
  const { hasPermissionsCheck } = useHasPermissionsCheck();
  const [searchValue, setSearchValue] = useState("");

  const allowedActions = actions.filter(
    ({ isHidden, permission }) =>
      !isHidden &&
      (!permission ||
        hasPermissionsCheck({
          entity: permission.entity,
          action: permission.action,
        })),
  );

  const isSearchVisible =
    allowedActions.length >= BULK_ACTIONS_PANEL_CONFIG.searchMinItems;
  const visibleActions = filterBulkActions(allowedActions, searchValue);

  return (
    <BulkActionsPanelStyled>
      {isSearchVisible && (
        <div className="bulk-actions-search">
          <FilterSearch
            searchValue={searchValue}
            onChange={setSearchValue}
            placeholder={t(`general.SEARCH_ACTIONS`)}
            fullWidth
          />
        </div>
      )}
      <ul className="bulk-actions-list">
        {visibleActions.map(action => (
          <BulkActionsRow key={action.key} action={action} />
        ))}
      </ul>
      {/* Only the search box can empty a non-empty list - a drawer with no permitted action
          at all stays quiet instead of blaming the search. */}
      {!visibleActions.length && Boolean(searchValue) && (
        <p className="bulk-actions-empty">{t(`general.NO_RESULTS`)}</p>
      )}
    </BulkActionsPanelStyled>
  );
};
